import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  LogOut,
  Menu,
  User as UserIcon,
  Bell,
  House,
  LayoutDashboard,
  PawPrint,
  Building2,
  ClipboardList,
  FileQuestion,
  FolderCheck,
  Users,
  Images,
  PanelsTopLeft,
  ChevronLeft,
  ChevronRight,
  Mail,
  Send,
  Search,
} from 'lucide-react';
import { adoptionApi } from '@/features/adoption/api';
import { PublicFooter } from '@/features/footer/public/PublicFooter';
import { shelterSignupsApi } from '@/features/shelter-signups/api';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface LayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
}

export function Layout({ children, showHeader = true, showFooter = false }: LayoutProps) {
  const { user, logout, isAdmin, isAdminWithShelter, isCustomer, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [newShelterSignupCount, setNewShelterSignupCount] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const stored = sessionStorage.getItem('sidebar_collapsed');
    return stored === '1';
  });
  const useSidebarMenu = showHeader && isAuthenticated && !isCustomer;
  const isSuperAdmin = isAdmin && !isAdminWithShelter;

  useEffect(() => {
    if (!showHeader) return;
    if (!isAuthenticated || isCustomer) return;
    const loadUnread = async () => {
      try {
        const res: any = await adoptionApi.listApplications({
          status: 'SUBMITTED',
          filter: { limit: 100, fields: ['id'] },
        });
        const count = Array.isArray(res) ? res.length : Number(res?.total ?? res?.data?.length ?? 0);
        setUnreadCount(Number.isFinite(count) ? count : 0);
      } catch {
        // ignore
      }
    };
    loadUnread();
  }, [showHeader, isAuthenticated, isCustomer]);

  useEffect(() => {
    if (!showHeader || !isAuthenticated || isCustomer || !isSuperAdmin) return;
    let cancelled = false;
    const loadCount = async () => {
      try {
        const count = await shelterSignupsApi.countNew();
        if (!cancelled) setNewShelterSignupCount(count);
      } catch {
        if (!cancelled) setNewShelterSignupCount(0);
      }
    };
    loadCount();
    const onFocus = () => {
      void loadCount();
    };
    window.addEventListener('focus', onFocus);
    const intervalId = window.setInterval(() => {
      void loadCount();
    }, 30000);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(intervalId);
    };
  }, [showHeader, isAuthenticated, isCustomer, isSuperAdmin]);

  useEffect(() => {
    sessionStorage.setItem('sidebar_collapsed', isSidebarCollapsed ? '1' : '0');
  }, [isSidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const customerNavLinks = [
    { to: '/adopter/dashboard', label: 'Mis adopciones' },
  ];

  const adminSidebarLinks = [
    { to: '/adopt', label: 'Home', icon: House },
    { to: '/dashboard', label: 'Panel', icon: LayoutDashboard },
    { to: '/animals', label: 'Animales', icon: PawPrint },
    { to: '/rescued-animals', label: 'Encontrados', icon: Search },
    { to: '/shelters', label: 'Entidades', icon: Building2 },
    ...(isSuperAdmin ? [{ to: '/admin/shelter-signups', label: 'Interesados', icon: Bell, badgeCount: newShelterSignupCount }] : []),
    ...(isAdmin ? [{ to: '/admin/forms', label: 'Formularios', icon: FileQuestion }] : []),
    { to: '/adoptions/manage', label: 'Solicitudes', icon: ClipboardList, showUnread: true },
    { to: '/adoption-wait-requests', label: 'Espera', icon: Bell },
    { to: '/animals/adopted', label: 'Adoptados', icon: FolderCheck },
    ...(isAdmin ? [{ to: '/users', label: 'Usuarios', icon: Users }] : []),
  ];

  const adminSettingsLinks = isSuperAdmin
    ? [
        { to: '/admin/banners', label: 'Banners', icon: Images },
        { to: '/admin/footer', label: 'Footer', icon: PanelsTopLeft },
        { to: '/admin/email-contacts', label: 'Contactos', icon: Mail },
        { to: '/admin/email-campaigns', label: 'Campañas de Email', icon: Send },
      ]
    : [];

  const isLinkActive = (to: string) => {
    if (to === '/dashboard') return location.pathname === '/dashboard';
    if (to === '/adopt') return location.pathname === '/adopt' || location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {showHeader && (
        <header className="border-b bg-card shadow-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {useSidebarMenu && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsSidebarCollapsed((prev) => !prev)}
                  aria-label={isSidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
                >
                  {isSidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
                </Button>
              )}

              <Link to={isCustomer ? '/adopter/dashboard' : '/dashboard'} className="flex items-center gap-2 font-bold text-xl">
                <div className="flex items-center justify-center">
                  <img src="/logo-sabuesos.png" alt="Sabuesos" className="h-8 w-8" />
                </div>
                <span>Sabuesos</span>
              </Link>
            </div>

            {!useSidebarMenu && (
              <nav className="hidden md:flex items-center gap-6">
                {customerNavLinks.map((link) => (
                  <Link key={link.to} to={link.to} className="text-sm hover:text-primary transition-colors relative">
                    {link.label}
                  </Link>
                ))}
              </nav>
            )}

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{user?.full_name}</span>
                      <span className="text-xs text-muted-foreground">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile">
                      <UserIcon className="mr-2 h-4 w-4" />
                      Mis datos
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
      )}

      {useSidebarMenu ? (
        <div className="flex flex-1 min-h-0">
          <aside
            className={cn(
              'border-r bg-card transition-all duration-300 ease-in-out',
              isSidebarCollapsed ? 'w-20' : 'w-64'
            )}
          >
            <div className="h-full overflow-y-auto py-4">
              <nav className="space-y-1 px-2">
                {adminSidebarLinks.map((link) => {
                  const Icon = link.icon;
                  const active = isLinkActive(link.to);
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      title={isSidebarCollapsed ? link.label : undefined}
                      className={cn(
                        'relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      {!isSidebarCollapsed && <span className="truncate">{link.label}</span>}
                      {(link.showUnread ? unreadCount : (link as any).badgeCount) > 0 && (
                        <span
                          className={cn(
                            'absolute inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white',
                            isSidebarCollapsed ? 'right-2 top-1' : 'right-2 top-2'
                          )}
                        >
                          {(link.showUnread ? unreadCount : (link as any).badgeCount) > 99 ? '99+' : (link.showUnread ? unreadCount : (link as any).badgeCount)}
                        </span>
                      )}
                    </Link>
                  );
                })}

                {adminSettingsLinks.length > 0 && (
                  <>
                    <div className={cn('pt-4 pb-1', isSidebarCollapsed ? 'px-0' : 'px-3')}>
                      <div
                        className={cn(
                          'text-[11px] uppercase tracking-wide text-muted-foreground/80',
                          isSidebarCollapsed ? 'text-center' : ''
                        )}
                      >
                        {isSidebarCollapsed ? '···' : 'Ajustes'}
                      </div>
                    </div>
                    {adminSettingsLinks.map((link) => {
                      const Icon = link.icon;
                      const active = isLinkActive(link.to);
                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          title={isSidebarCollapsed ? link.label : undefined}
                          className={cn(
                            'relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                            active
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          )}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          {!isSidebarCollapsed && <span className="truncate">{link.label}</span>}
                        </Link>
                      );
                    })}
                  </>
                )}
              </nav>
            </div>
          </aside>

          <main className="flex-1 bg-muted/30 min-w-0">
            {children}
          </main>
        </div>
      ) : (
        <main className="flex-1 bg-muted/30">
          {children}
        </main>
      )}

      {showFooter && (
        <footer className="border-t bg-card py-8">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-center gap-4">
              <PublicFooter />
              <div className="text-center text-sm text-muted-foreground border-t w-full pt-4 mt-2">
                © {new Date().getFullYear()} Sabuesos - Sistema de Gestión de Adopciones
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
